// Search only near each existing side. Long coherent edges outrank isolated decoration.
export function snapRectToEdges({ data, width, height }, rect, radius, bounds = [0, 0, width, height], skipDarkBorder = true) {
  if (!rect.every(Number.isFinite) || rect[0] >= rect[2] || rect[1] >= rect[3]) return rect;
  const pixel = (x, y, c) => {
    const i = (y * width + x) * 4, alpha = data[i + 3] / 255;
    return data[i + c] * alpha + 255 * (1 - alpha);
  };
  const next = rect.map((position, side) => {
    const axis = side % 2, cross = 1 - axis;
    const length = rect[cross + 2] - rect[cross];
    const from = Math.max(0, Math.ceil(rect[cross] + length * 0.1));
    const to = Math.min((axis === 0 ? height : width) - 1, Math.floor(rect[cross + 2] - length * 0.1));
    const step = Math.max(1, Math.ceil((to - from) / 256));
    let best = position, score = 0;
    for (let at = Math.max(1, Math.ceil(position - radius), Math.ceil(bounds[axis]));
      at <= Math.min((axis === 0 ? width : height) - 1, Math.floor(position + radius), Math.floor(bounds[axis + 2])); at++) {
      let total = 0, hits = 0, count = 0;
      for (let along = from; along <= to; along += step) {
        let difference = 0;
        for (let c = 0; c < 3; c++) difference += Math.abs(axis === 0
          ? pixel(at, along, c) - pixel(at - 1, along, c)
          : pixel(along, at, c) - pixel(along, at - 1, c));
        difference /= 3;
        total += difference; hits += difference >= 8 ? 1 : 0; count++;
      }
      if (!count || hits / count < 0.55) continue;
      const candidate = total / count / (1 + 0.6 * Math.abs(at - position) / Math.max(1, radius));
      if (candidate > score || (candidate === score && Math.abs(at - position) < Math.abs(best - position))) {
        score = candidate; best = at;
      }
    }
    if (skipDarkBorder) {
      // A panel-to-panel shadow has two edges. The strongest may be the far edge
      // of the black gap, leaving the gap inside the crop. Find a dark valley
      // near the edge. Stop at the black gap's exit, not at the bright face:
      // the rounded/bevelled shoulder belongs to the panel and must be retained.
      const direction = side < 2 ? 1 : -1;
      const atPixel = offset => Math.round(best) + (side < 2 ? offset : -1 - offset);
      const insideEnd = side < 2 ? rect[axis + 2] : rect[axis];
      const available = Math.floor(Math.abs(insideEnd - best));
      if (available >= 40) {
        const light = (offset, along) => {
          const at = atPixel(offset);
          if (at < 0 || at >= (axis === 0 ? width : height)) return null;
          const x = axis === 0 ? at : along, y = axis === 0 ? along : at;
          return .2126 * pixel(x,y,0) + .7152 * pixel(x,y,1) + .0722 * pixel(x,y,2);
        };
        const recoveries = [];
        let samples = 0;
        for (let along = from; along <= to; along += step) {
          samples++;
          const referenceAt = Math.min(30, Math.floor(available / 2));
          const reference = [referenceAt-2,referenceAt-1,referenceAt].map(offset => light(offset,along));
          if (reference.some(v => v == null)) continue;
          reference.sort((a,b) => a-b);
          const surface = reference[1];
          const near = Array.from({length:11},(_,offset)=>light(offset,along));
          if (near.some(v=>v==null)) continue;
          const darkest = Math.min(...near), valley = near.indexOf(darkest);
          if (darkest > 80 || darkest > surface * .7 || surface-darkest < 20) continue;
          if (valley > 3 && near[0] > darkest + (surface-darkest)*.4) continue;
          const gapExit = darkest + Math.max(12,(surface-darkest)*.25);
          for (let offset = valley+1; offset <= 10; offset++) {
            // Only remove the narrow black core; keep the lighter bevel and shadow.
            if ([0,1,2].every(extra => light(offset+extra,along) >= gapExit)) {
              recoveries.push(offset); break;
            }
          }
        }
        if (samples && recoveries.length / samples >= .7) {
          recoveries.sort((a,b) => a-b);
          best += direction * recoveries[Math.floor((recoveries.length-1) * .5)];
        }
      }
    }
    return best;
  });
  return next[0] < next[2] && next[1] < next[3] ? next : rect;
}

export function snapImageFrames(image, regions, radius) {
  const canvas = document.createElement("canvas");
  canvas.width = image.naturalWidth; canvas.height = image.naturalHeight;
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) throw new Error("A böngésző nem tudta elindítani az élkeresést.");
  context.drawImage(image, 0, 0);
  const pixels = context.getImageData(0, 0, canvas.width, canvas.height);
  return regions.map(region => {
    let rect = snapRectToEdges(pixels, region.rect, radius);
    const cutouts = (region.cutouts || []).map(cutout => ({ ...cutout,
      rect: snapRectToEdges(pixels, cutout.rect, radius, rect, false) }));
    // A weak outer edge must never clip an existing inner selection.
    if (cutouts.some(c => c.rect[0] < rect[0] || c.rect[1] < rect[1] || c.rect[2] > rect[2] || c.rect[3] > rect[3])) {
      rect = region.rect;
      return { ...region, rect };
    }
    return { ...region, rect, cutouts };
  });
}
